import React, { useState, useEffect, useCallback } from 'react';
import '../styles/react-colorful.css';
import { HexColorPicker } from 'react-colorful';
import { useRef } from 'react';
import { Stream } from '../services/streamService';
import streamService from '../services/streamService';
import StreamItem from './StreamItem';
import { Device } from '../services/deviceService';
import deviceService from '../services/deviceService';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

interface StreamListProps {
  device: Device;
}

export const StreamList: React.FC<StreamListProps> = ({ device }) => {
  const [streams, setStreams] = useState<Stream[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newChannelId, setNewChannelId] = useState('');
  const [newChannelType, setNewChannelType] = useState<'twitch' | 'youtube' | 'kick'>('twitch');
  const [newColor, setNewColor] = useState('#9146ff');
  const [newBlink, setNewBlink] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deletingStreamId, setDeletingStreamId] = useState<number | null>(null);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const pickerRef = useRef<HTMLDivElement | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 100,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const loadStreams = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const deviceStreams = await streamService.getByDeviceId(device.id);
      // Sort by priority (lower number = higher priority = shown first)
      const sorted = deviceStreams.sort((a, b) => a.priority - b.priority);
      setStreams(sorted);
    } catch (err: any) {
      console.error('Failed to load streams:', err);
      setError('Failed to load streams');
      setStreams([]);
    } finally {
      setLoading(false);
    }
  }, [device.id]);

  useEffect(() => {
    loadStreams();
  }, [loadStreams]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (showColorPicker && pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setShowColorPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showColorPicker]);

  const handleOpenAddModal = () => {
    setShowAddModal(true);
    setNewChannelId('');
    setNewChannelType('twitch');
    setNewColor('#9146ff');
    setNewBlink(false);
    setError('');
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setNewChannelId('');
    setNewColor('#9146ff');
    setNewBlink(false);
    setShowColorPicker(false);
    setError('');
  };

  const handleAddStream = async () => {
    if (!newChannelId.trim()) {
      setError('Channel ID is required');
      return;
    }

    if (streams.length >= 5) {
      setError('Maximum of 5 streams per device reached');
      return;
    }

    // For Single Color devices, use white (255, 255, 255)
    // For RGB devices, convert hex color to RGB
    let r = 255, g = 255, b = 255;
    
    if (device.device_type === 'RGB') {
      const hex = newColor.replace('#', '');
      r = parseInt(hex.substring(0, 2), 16);
      g = parseInt(hex.substring(2, 4), 16);
      b = parseInt(hex.substring(4, 6), 16);
    }

    setError('');
    setCreating(true);

    try {
      const newStream = await streamService.create(device.id, newChannelId, newChannelType, r, g, b, newBlink);
      setStreams([...streams, newStream]);
      handleCloseAddModal();
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to create stream';
      setError(message);
    } finally {
      setCreating(false);
    }
  };

  const handlePreview = async () => {
    setError('');
    setPreviewing(true);

    try {
      if (device.device_type === 'RGB') {
        // For RGB devices, convert hex color to RGB
        const hex = newColor.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        await deviceService.previewColor(device.id, r, g, b, newBlink);
      } else {
        // For non-RGB devices, use status: 0 = off, 1 = on, 2 = blinking
        const status = newBlink ? 2 : 1;
        await deviceService.previewLight(device.id, status as 0 | 1 | 2);
      }
      console.log('Preview sent to device');
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to preview on device';
      setError(message);
    } finally {
      setPreviewing(false);
    }
  };

  const handleDeleteStream = async (streamId: number) => {
    if (!window.confirm('Are you sure you want to delete this stream?')) {
      return;
    }

    setError('');
    setDeletingStreamId(streamId);

    try {
      await streamService.delete(streamId);
      setStreams(streams.filter(s => s.id !== streamId));
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to delete stream';
      setError(message);
    } finally {
      setDeletingStreamId(null);
    }
  };

  const handleUpdateStream = (updatedStream: Stream) => {
    setStreams(streams.map(s => s.id === updatedStream.id ? updatedStream : s));
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = streams.findIndex(s => s.id === active.id);
    const newIndex = streams.findIndex(s => s.id === over.id);

    const newStreams = arrayMove(streams, oldIndex, newIndex);
    setStreams(newStreams);

    // Update priorities in background without refreshing
    const updates = newStreams.map((stream, index) => {
      const newPriority = index + 1;
      return streamService.update(stream.id, { priority: newPriority });
    });

    try {
      await Promise.all(updates);
    } catch (err) {
      console.error('Failed to update priorities:', err);
      setError('Failed to update priorities');
    }
  };

  return (
    <div className="stream-list-container">
      {streams.length >= 5 && (
        <div style={{ padding: '8px 12px', backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: 4, marginBottom: 12, fontSize: 14 }}>
          ⚠️ Maximum of 5 streams per device reached
        </div>
      )}

      {/* Add Stream Button */}
      <div style={{ marginBottom: 20 }}>
        <button
          onClick={handleOpenAddModal}
          disabled={streams.length >= 5}
          className="btn-add-stream"
          type="button"
          title={streams.length >= 5 ? 'Maximum 5 streams per device' : 'Add a new stream'}
          style={{
            width: '100%',
            padding: '12px',
            fontSize: 15,
            fontWeight: 600,
          }}
        >
          {streams.length >= 5 ? '⚠️ Max Streams (5)' : '+ Add Stream'}
        </button>
      </div>

      {/* Add Stream Modal */}
      {showAddModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={handleCloseAddModal}
        >
          <div
            style={{
              backgroundColor: 'var(--bg-card)',
              padding: 30,
              borderRadius: 12,
              maxWidth: 450,
              width: '90%',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, marginBottom: 20, color: 'var(--text-primary)' }}>
              Add New Stream
            </h3>

            {error && <div className="error-message" style={{ marginBottom: 15 }}>{error}</div>}

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', marginBottom: 8, color: 'var(--text-primary)', fontWeight: 600 }}>
                Platform
              </label>
              <select
                value={newChannelType}
                onChange={(e) => setNewChannelType(e.target.value as 'twitch' | 'youtube' | 'kick')}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: 6,
                  border: '1px solid var(--border-color)',
                  backgroundColor: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  fontSize: 14,
                  boxSizing: 'border-box',
                }}
                disabled={creating}
              >
                <option value="twitch">Twitch</option>
                <option value="youtube">YouTube</option>
                <option value="kick">Kick</option>
              </select>
              {newChannelType === 'youtube' && (
                <div style={{ 
                  marginTop: 10, 
                  padding: '10px', 
                  backgroundColor: 'var(--bg-secondary)', 
                  border: '1px solid var(--border-color)',
                  borderRadius: 6,
                  fontSize: 13,
                  color: 'var(--text-secondary)',
                  lineHeight: '1.5'
                }}>
                  💡 <strong>Tip:</strong> Get YouTube channel ID at{' '}
                  <a 
                    href="https://yt-link.com/stats" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ color: '#667eea', textDecoration: 'underline' }}
                  >
                    yt-link.com/stats
                  </a>
                  {' '}by searching for the channel, then expand the arrow underneath the channel name.
                </div>
              )}
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', marginBottom: 8, color: 'var(--text-primary)', fontWeight: 600 }}>
                Channel ID
              </label>
              <input
                type="text"
                value={newChannelId}
                onChange={(e) => setNewChannelId(e.target.value)}
                placeholder="Enter channel ID"
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: 6,
                  border: '1px solid var(--border-color)',
                  backgroundColor: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  fontSize: 14,
                  boxSizing: 'border-box',
                }}
                disabled={creating}
              />
            </div>

            {device.device_type === 'RGB' && (
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 8, color: 'var(--text-primary)', fontWeight: 600 }}>
                  LED Color
                </label>
                <HexColorPicker color={newColor} onChange={setNewColor} style={{ width: '100%' }} />
                <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 8,
                      backgroundColor: newColor,
                      border: '2px solid var(--border-color)',
                    }}
                  />
                  <span style={{ color: 'var(--text-primary)', fontFamily: 'monospace' }}>
                    {newColor.toUpperCase()}
                  </span>
                </div>
              </div>
            )}

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', color: 'var(--text-primary)' }}>
                <input
                  type="checkbox"
                  checked={newBlink}
                  onChange={(e) => setNewBlink(e.target.checked)}
                  style={{ width: 18, height: 18, cursor: 'pointer' }}
                />
                <span style={{ fontWeight: 600 }}>Enable Blinking</span>
              </label>
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between', alignItems: 'center' }}>
              <button
                onClick={handlePreview}
                disabled={creating || previewing}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'transparent',
                  color: '#667eea',
                  border: '1px solid #667eea',
                  borderRadius: 6,
                  cursor: (creating || previewing) ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                }}
                title="Preview this color on your device"
              >
                {previewing ? '⏳ Previewing...' : '👁️ Preview'}
              </button>
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  onClick={handleCloseAddModal}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: 'transparent',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontWeight: 600,
                  }}
                  disabled={creating}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddStream}
                  disabled={creating}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: 6,
                    cursor: creating ? 'not-allowed' : 'pointer',
                    fontWeight: 600,
                  }}
                >
                  {creating ? 'Adding...' : 'Add Stream'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Streams List */}
      {loading ? (
        <div className="loading-streams">Loading streams...</div>
      ) : streams.length === 0 ? (
        <div className="no-streams">No streams yet. Create one above!</div>
      ) : (
        <div className="streams-list">
          <div style={{ marginBottom: 12, fontSize: 13, color: '#666' }}>
            💡 Drag streams to reorder priority (top = highest priority)
          </div>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={streams.map(s => s.id)}
              strategy={verticalListSortingStrategy}
            >
              {streams.map((stream, index) => (
                <StreamItem
                  key={stream.id}
                  stream={stream}
                  onDelete={handleDeleteStream}
                  onUpdate={handleUpdateStream}
                  isDeleting={deletingStreamId === stream.id}
                  priorityRank={index + 1}
                  deviceId={device.id}
                  deviceType={device.device_type}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      )}
    </div>
  );
};

export default StreamList;
